import React from "react";
import Typography from "@mui/material/Typography";
import { ErrorLevel, ValidationResult } from "./Interfaces";
import { optionalEss } from "./UtilityMethods";
import { CHARGES, CREW, CUSTOMER, FLIGHT_LEGS, FUEL, LANDINGS, MINIMUMS, NOTES, PASSENGERS, PER_DIEMS } from "./TicketValidator";

export function getDescriptiveName(newUrl: string): string {
    switch (newUrl) {
        case "ticket/charges":
            return CHARGES;
        case "ticket/crew":
            return CREW;
        case "ticket/customer":
            return CUSTOMER;
        case "ticket/legs":
            return FLIGHT_LEGS;
        case "ticket/fuel":
            return FUEL;
        case "ticket/landings":
            return LANDINGS;
        case "ticket/minimums":
            return MINIMUMS;
        case "ticket/notes":
            return NOTES;
        case "ticket/pax":
            return PASSENGERS;
        case "ticket/perdiems":
            return PER_DIEMS;
    }

    return newUrl;
}

function getNewCardName(cardName: string): string {
    switch (cardName) {
        case CHARGES:
            return "ticket/charges";
        case CREW:
            return "ticket/crew";
        case CUSTOMER:
            return "ticket/customer";
        case FLIGHT_LEGS:
            return "ticket/legs";
        case FUEL:
            return "ticket/fuel";
        case LANDINGS:
            return "ticket/landings";
        case MINIMUMS:
            return "ticket/minimums";
        case NOTES:
            return "ticket/notes";
        case PASSENGERS:
            return "ticket/pax";
        case PER_DIEMS:
            return "ticket/perdiems";
    }

    return cardName;
}

export class TicketErrorHelper {
    static hasErrors(validationMessages: ValidationResult[], fieldId: string): boolean {
        const message = validationMessages.find((validation) => {
            return validation.propertyName === fieldId;
        });
        return message !== undefined;
    }

    static childFieldHasErrors(validationMessages: ValidationResult[], fieldId: string, entityId: number): boolean {
        const message = validationMessages.find((validation) => {
            return validation.propertyName === fieldId && validation.entityId === entityId;
        });
        return message !== undefined;
    }

    static getChildFieldErrors(validationMessages: ValidationResult[], fieldId: string, entityId: number): string {
        const messages = validationMessages
            .filter((validation) => {
                return validation.propertyName === fieldId && validation.entityId === entityId;
            })
            .map((v) => v.message);
        const result = messages.join("; ");
        return result;
    }

    static getErrorMessagesForCardRow(validationMessages: ValidationResult[], cardName: string, entityId: number): ValidationResult[] {
        const messages = validationMessages.filter((validation) => {
            return validation.editCard === getNewCardName(cardName) && validation.entityId === entityId;
        });
        return messages;
    }

    static getErrorMessages(validationMessages: ValidationResult[], fieldId: string): string {
        const message = validationMessages.find((validation) => {
            return validation.propertyName === fieldId;
        });
        return message === undefined ? "" : message.message;
    }

    static getCardErrors(validationMessages: ValidationResult[], cardName: string): JSX.Element {
        const applicableMessages = validationMessages.filter((vr) => vr.editCard === getNewCardName(cardName));

        if (applicableMessages.length > 1) {
            return this.getFormattedErrorCounts(applicableMessages);
        }

        return this.formattedErrorList(applicableMessages);
    }

    static getAllErrors(validationMessages: ValidationResult[]): JSX.Element {
        if (validationMessages.length > 1) {
            return this.getFormattedErrorCounts(validationMessages);
        }

        return this.formattedErrorList(validationMessages);
    }

    static getFormattedErrorCounts(errorList: ValidationResult[]): JSX.Element {
        const errorCount = errorList.filter((m) => m.level === ErrorLevel.Error).length;
        const warningCount = errorList.filter((m) => m.level === ErrorLevel.Warn).length;

        return (
            <>
                {errorCount > 0 && (
                    <Typography display={"inline"} color={"error"} textAlign={"center"}>
                        {`${errorCount} ERROR`}
                        {optionalEss(errorCount)}
                    </Typography>
                )}
                {errorCount > 0 && warningCount > 0 && (
                    <Typography display={"inline"} color={"warning.main"} textAlign={"center"}>
                        {` AND `}
                    </Typography>
                )}
                {warningCount > 0 && (
                    <Typography display={"inline"} color={"warning.main"} textAlign={"center"}>
                        {`${warningCount} WARNING`}
                        {optionalEss(warningCount)}
                    </Typography>
                )}
            </>
        );
    }

    static isApprovable(validationMessages: ValidationResult[]): boolean {
        return !TicketErrorHelper.containsErrors(validationMessages);
    }

    static containsErrors(validationMessages: ValidationResult[]): boolean {
        return validationMessages.filter((m) => m.level === ErrorLevel.Error).length > 0;
    }

    private static formattedErrorList(applicableErrors: ValidationResult[]) {
        const formattedErrors: JSX.Element[] = [];

        applicableErrors.forEach((vr) =>
            formattedErrors.push(
                <Typography key={vr.message} textAlign={"center"} color={vr.level === ErrorLevel.Error ? "error" : "warning.main"}>
                    {vr.message}
                </Typography>
            )
        );

        return <>{formattedErrors}</>;
    }
}
